import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useStateContext } from "state";
import { useProjects, useFavoriteProjects } from "hooks/projects";
import { useFuzzySearch } from "core/hooks/useFuzzySearch";
import { IProjectsTableData, ProjectsTable } from "features/ad-hoc-projects/projects-table";
import { ProjectsError } from "features/ad-hoc-projects/projects-error";
import { FavoriteProjectsError } from "features/ad-hoc-projects/favorite-projects-error";
import { FavoriteProjectsZeroState } from "features/ad-hoc-projects/favorite-projects-zero-state";
import { ProjectSearch } from "features/ad-hoc-projects/projects-search";
import 'features/ad-hoc-projects/projects.page.scss'

export function ProjectsPage() {
  const { user } = useStateContext();
  const { id } = useParams();
  const [searchTerm, setSearchTerm] = useState('');

  // we have two paths here but, only one is provided in the route when there are multiple accounts
  const account_number = user?.isSingleAdHocAccount ?
    parseInt(user.roles[0].accountNumber) :
    parseInt(id!)

  const { data: projects, isFetching: isProjectsFetching, isError: isProjectsError } = useProjects(account_number);
  const { data: favoriteProjects, isFetching: isFavoriteProjectsFetching, isError: isFavoriteProjectsError } = useFavoriteProjects(user?.id!, account_number);

  // memoize the favorite ids in a set for optimized lookup
  const favoriteProjectIds = useMemo(() => {
    const ids = favoriteProjects?.map(fav => fav.id) || []
    return new Set(ids)
  }, [favoriteProjects])

  // memoize all the projects and flag the favorites
  const projectsWithFavorites = useMemo(() => {
    if (!projects)
      return new Array<IProjectsTableData>();

    return projects.map(project => ({
      isFavorite: favoriteProjectIds.has(project.id),
      ...project
    }))
  }, [projects, favoriteProjectIds])

  // filter the list of memoized projects with favorites
  const filteredProjects = useFuzzySearch(projectsWithFavorites, searchTerm, {
    keys: ['id', 'order_number'],
    ignoreLocation: true,
    threshold: 0
  })

  const noFavoriteProjects = !isFavoriteProjectsFetching && (!favoriteProjects || favoriteProjects.length === 0);

  // get the table data to display
  const tableData = useMemo(() => {

    // if we are searching, just return the search results
    if (searchTerm.length > 0)
      return filteredProjects.map(result => result.item)

    // if we aren't searching and there are no favorite projects, make sure the table gets an empty array
    if (noFavoriteProjects)
      return new Array<IProjectsTableData>();

    // if we aren't searching and there are favorite projects, filter them out and return just those
    return projectsWithFavorites.filter(project => project.isFavorite)
  }, [searchTerm, filteredProjects, projectsWithFavorites, noFavoriteProjects])

  // render flags
  const shouldRenderFavoriteProjectsZeroState = searchTerm.length === 0 && noFavoriteProjects
  const shouldRenderProjectsTable = !isProjectsError && !isFavoriteProjectsError && !shouldRenderFavoriteProjectsZeroState

  return (
    <div className='projects-page'>
      <h2>Projects</h2>
      <ProjectSearch searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      {isProjectsError && <ProjectsError/>}
      {isFavoriteProjectsError && <FavoriteProjectsError/>}
      {shouldRenderFavoriteProjectsZeroState && <FavoriteProjectsZeroState/>}
      {shouldRenderProjectsTable && <ProjectsTable userId={user?.id!} accountNumber={account_number} dataSource={tableData} loading={isProjectsFetching || isFavoriteProjectsFetching} />}
    </div>
  );
}